import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	first,
	map,
	Observable,
	shareReplay,
	skip,
	Subscription,
	switchMap,
} from 'rxjs';
import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { UiConfig } from '@evasys/globals/evainsights/models/common/ui-config.model';
import { HttpClient } from '@angular/common/http';
import { endpoints } from '@evasys/globals/evainsights/api/endpoints';
import { TranslocoService } from '@ngneat/transloco';
import { DateTime } from '../../../../../../../shared/ui/src/lib/components/control-components/calendar/src/app/calendar/lib/utils/date';
import { AuthUserServiceHelperService } from '../auth/auth-user-service-helper.service';
import { runtimeEnvironment } from '@evasys/globals/evainsights/environments/runtime-environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
	providedIn: 'root',
})
export class UiConfigService {
	//region Injections
	private readonly http = inject(HttpClient);
	private readonly authUserServiceHelperService = inject(AuthUserServiceHelperService);
	private readonly translocoService = inject(TranslocoService);
	private readonly environmentInjector = inject(EnvironmentInjector);
	//endregion

	//region Variables
	private _uiConfig$ = new BehaviorSubject<UiConfig>({
		logoSrc: '/assets/logos/evasys.svg',
		decimalFormat: ',',
		dateFormat: 'd.m.Y',
		language: 'de_edu',
		timeFormat: 'H:i:s',
	});
	uiConfigUpdaterSubscription?: Subscription;

	//endregion
	public readonly isInitialized = this._uiConfig$.pipe(
		skip(1),
		first(),
		map(() => true),
		shareReplay()
	);

	//region Getter & Setter

	public getUiConfig(): Observable<UiConfig> {
		return this._uiConfig$.asObservable();
	}

	public set uiConfigLanguage(language: string) {
		this.isInitialized.subscribe(() => {
			this._uiConfig$.next({ ...this._uiConfig$.value, language });
		});
	}

	public getTimeDateFormat() {
		return combineLatest([this._uiConfig$, this.translocoService.selectTranslate('common.at')]).pipe(
			map(
				([config, atTranslation]) =>
					`${DateTime.toISOFormat(config.dateFormat)} '${atTranslation}' ${DateTime.toISOFormat(
						config.timeFormat
					)}`
			)
		);
	}
	//endregion

	//region Methods
	public keepUiConfigUpToDate(useSystemUiConfig = false) {
		this.uiConfigUpdaterSubscription?.unsubscribe();
		runInInjectionContext(this.environmentInjector, () => {
			this.uiConfigUpdaterSubscription = this.authUserServiceHelperService
				.getUserIdFromAuthUser()
				.pipe(
					distinctUntilChanged(),
					switchMap((value) =>
						this.http.get<UiConfig>(
							useSystemUiConfig
								? endpoints.getSystemUiConfig.url({
										tenantSubdomain: runtimeEnvironment.tenantSubdomain,
								  })
								: endpoints.getUserUiConfig.url()
						)
					),
					takeUntilDestroyed()
				)
				.subscribe((uiConfig) => {
					this._uiConfig$.next({
						...uiConfig,
						logoSrc:
							uiConfig.logoSrc != null && uiConfig.logoSrc.trim() !== ''
								? uiConfig.logoSrc
								: this._uiConfig$.value.logoSrc,
					});
				});
		});
	}

	//endregion
}
